import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";
import api, { formatApiError } from "../lib/api";

export default function Audit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", business: "", website_url: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.post("/track", { event: "audit_viewed" }).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.website_url) { toast.error("Name, email and website URL are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/audit", form);
      setResult(data);
      toast.success("Your audit is ready!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="audit-page" className="pt-32 pb-24 min-h-screen">
      <Seo page="home" />
      <Container>
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel><Search className="inline h-3.5 w-3.5 mr-1" /> Free AI Website Audit</SectionLabel>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F3EDDC]">Get your free AI website audit</h1>
            <p className="mt-4 text-lg text-[#B7AF98]">We'll analyze your website for mobile experience, SEO basics, contact clarity and conversion opportunities — free.</p>
          </div>
        </Reveal>

        <div className="mt-12 max-w-2xl mx-auto">
          {!result ? (
            <form onSubmit={submit} data-testid="audit-form" className="rounded-3xl border border-white/10 bg-[#141414] p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Your name *</Label><Input data-testid="audit-name" value={form.name} onChange={set("name")} /></div>
                <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Email *</Label><Input data-testid="audit-email" value={form.email} onChange={set("email")} /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Business name</Label><Input data-testid="audit-business" value={form.business} onChange={set("business")} /></div>
              <div className="space-y-1.5"><Label className="text-[#D2CAB2]">Website URL *</Label><Input data-testid="audit-url" value={form.website_url} onChange={set("website_url")} placeholder="https://yourbusiness.com" /></div>
              <Button data-testid="audit-submit" type="submit" disabled={loading} className="w-full rounded-full h-12 gold-shimmer-btn bg-[#C9A227] text-black">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing your site…</> : <>Get my free audit <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
              <p className="text-xs text-[#7E7663] text-center flex items-center justify-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> No spam. We'll only use your details to send your audit and follow up.</p>
            </form>
          ) : (
            <div data-testid="audit-result" className="rounded-3xl border border-white/10 bg-[#141414] p-8">
              <h2 className="font-display text-2xl font-bold text-[#F3EDDC]">Your website audit</h2>
              <p className="text-sm text-[#7E7663] mt-1 break-all">{result.signals?.url || form.website_url}</p>
              <div data-testid="audit-report" className="mt-5 whitespace-pre-wrap text-[#D2CAB2] text-sm leading-relaxed">{result.report}</div>
              <div className="mt-8 rounded-2xl bg-black border border-[#C9A227]/30 p-6 text-center">
                <h3 className="font-display text-xl font-bold text-white">Want us to build or improve your website?</h3>
                <p className="mt-2 text-white/70 text-sm">Our AI Advisor will recommend the perfect package in under a minute.</p>
                <Button data-testid="audit-start-project" onClick={() => navigate("/build")} className="mt-4 rounded-full gold-shimmer-btn bg-[#C9A227] text-black h-11 px-6">
                  Start your WebLaunch project <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
